import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import CustomDatatable from "../CustomDatatable/CustomDatatable";
import type { ColumnMeta } from "../CustomDatatable/CustomDatatable";
import StatusTag from "../StatusTag/StatusTag";
import { InstanceDialog } from "../Dialogs/AccessDialogs";
import { useInstancesAPI } from "../../hooks/api/use-accessAPI";
import { ProjectInstance } from "../../store/types";
import { BodyText } from "../../styles/Typography";
import { IconPlus } from "../../assets/icons/Icon";
import { FlexLineJustify, Label } from "../../styles/PageLayoutStyles.styles";
import { instancePath } from "./accessOptions";
import { SectionText } from "./Access.styles";

type Option = { label: string; value: any };

/**
 * Инстансы продукта (страница продукта) или компании (страница компании).
 * Продукт или компания страницы подставляются в форму создания сами.
 */
const InstancesSection: React.FC<{
  projectId?: string;
  organizationId?: number;
  projectsOptions?: Option[];
  companiesOptions?: Option[];
}> = ({ projectId, organizationId, projectsOptions, companiesOptions }) => {
  const navigate = useNavigate();
  const { instances, handleCreateInstance, toastRef } = useInstancesAPI({
    projectId,
    organizationId,
  });
  const [dialogVisible, setDialogVisible] = useState(false);

  const open = (instance: ProjectInstance) => navigate(instancePath(instance.id));

  const columns: ColumnMeta<ProjectInstance>[] = [
    { field: "id", header: "Id", sortable: true, body: (x) => <code>{x.id}</code> },
    projectId
      ? { field: "organizationName", header: "Компания", sortable: true }
      : { field: "projectName", header: "Продукт", sortable: true },
    { field: "description", header: "Описание", body: (x) => x.description || "—" },
    {
      field: "isEnabled",
      header: "Статус",
      body: (x) =>
        x.isEnabled ? (
          <StatusTag status="success" label="Включён" />
        ) : (
          <StatusTag status="danger" label="Выключен" />
        ),
    },
    {
      field: "clientId",
      header: "Клиент",
      body: (x) =>
        x.clientId ? (
          <StatusTag status="info" label="Есть" />
        ) : (
          <StatusTag status="secondary" label="Нет" />
        ),
    },
  ];

  return (
    <>
      <Toast ref={toastRef} position="bottom-center" />
      <FlexLineJustify>
        <SectionText>
          <BodyText>
            Инсталляции продуктов у компаний. Сотрудники компании инстанса входят в него без
            ролей, остальным нужна глобальная роль на инстансе.
          </BodyText>
        </SectionText>
        <Button onClick={() => setDialogVisible(true)}>
          <IconPlus size={16} />
          <Label>Новый инстанс</Label>
        </Button>
      </FlexLineJustify>

      <CustomDatatable<ProjectInstance>
        data={instances}
        columns={columns}
        onView={open}
        onRowDoubleClick={open}
        getRowId={(x) => x.id}
        hideViewButton={false}
        hideEditButton
        hideDeleteButton
        hideContextMenu
        sortable
      />

      <InstanceDialog
        visible={dialogVisible}
        fixedProjectId={projectId}
        fixedOrganizationId={organizationId}
        projectsOptions={projectsOptions}
        companiesOptions={companiesOptions}
        onHide={() => setDialogVisible(false)}
        onCreate={async (data) => {
          const created = await handleCreateInstance(data);
          if (created) navigate(instancePath(created.id));
          return !!created;
        }}
      />
    </>
  );
};

export default InstancesSection;
